import React, { useEffect, useRef, useState } from 'react';
import './KPICard.css';
import { formatYAxisNumber } from '../charts/utils/ChartConstants';

interface KeyMetricCardProps {
  color: string;
  title: string;
  value: string | number;
  measurement?: string;
  subtitle?: string;
  backgroundColor?: string;
}

const KeyMetricCard: React.FC<KeyMetricCardProps> = ({
  color,
  title,
  value,
  measurement,
  subtitle,
  backgroundColor
}) => {
  const valueRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Determine background color based on the provided color
  const getBackgroundColor = () => {
    // If color is a hex code, create a lighter version
    if (color.startsWith('#')) {
      // Convert to a lighter shade by adding opacity
      return `${color}15`; // Adds 15% opacity for light background
    }
    // Otherwise return a default light version
    return '#F5F5F5';
  };

  // Format the value for display
  const formatValue = (val: string | number): string => {
    // If it's already a formatted string with suffixes, return as-is
    const valStr = String(val).trim();
    if (valStr.includes('mln') || valStr.includes('bn') || valStr.includes('tn') ||
        valStr.includes('M') || valStr.includes('k') || valStr.includes('%') ||
        valStr.includes('High') || valStr.includes('Medium') || valStr.includes('Low') ||
        valStr.includes('Critical') || valStr.includes('/')) {
      return valStr;
    }

    // Try to parse and format as a number
    const num = parseFloat(valStr.replace(/[,$£€¥]/g, ''));
    if (!isNaN(num) && isFinite(num)) {
      return formatYAxisNumber(num);
    }

    // Return as-is if not a number
    return valStr;
  };

  // Check if value is numeric/short (good for large font)
  const isNumericOrShort = (val: string | number): boolean => {
    const valStr = String(val).trim();
    // Check if it's a number with common suffixes/prefixes
    const numericPattern = /^[\$£€¥]?[\d,]+\.?\d*[%MBKkmb]?$/;
    // Check if it's short text (5 words or fewer, 20 characters or fewer)
    const isShort = valStr.split(' ').length <= 5 && valStr.length <= 20;
    // Check if it's a simple rating or status
    const isRating = /^(High|Medium|Low|Critical|\d+\/\d+|\d+\.\d+\/\d+)$/i.test(valStr);

    return numericPattern.test(valStr) || isRating || (isShort && !valStr.includes(','));
  };

  // Calculate scale to fit text within container
  useEffect(() => {
    const calculateScale = () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (!valueRef.current || !containerRef.current) return;

        const container = containerRef.current;
        const valueElement = valueRef.current;

        // Reset scale to get true measurements
        valueElement.style.transform = 'none';

        // Force a reflow to ensure the browser recalculates
        void valueElement.offsetWidth;

        // Get measurements
        const containerWidth = container.offsetWidth;
        const valueWidth = valueElement.scrollWidth;

        // Calculate available width (container width - padding - buffer for measurement unit)
        const paddingTotal = 32; // 16px padding on each side
        const bufferForUnit = measurement ? 40 : 20; // Extra space if there's a measurement unit
        const availableWidth = containerWidth - paddingTotal - bufferForUnit;

        // Calculate scale if text is too wide
        if (valueWidth > availableWidth && availableWidth > 0) {
          const newScale = Math.max(0.5, Math.min(1, availableWidth / valueWidth));
          valueElement.style.transform = `scale(${newScale})`;
          setScale(newScale);
        } else {
          valueElement.style.transform = 'scale(1)';
          setScale(1);
        }
      }, 0);
    };

    // Calculate on mount and value change
    calculateScale();

    // Recalculate on window resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateScale, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [value, measurement]);

  return (
    <div
      className="metric-card"
      style={{ backgroundColor: backgroundColor || getBackgroundColor() }}
      ref={containerRef}
    >
      <div className="metric-card-label">{title}</div>
      <div className="metric-card-value-container">
        <span
          className={`metric-main-value`}
          ref={valueRef}
          style={{
            color,
            transformOrigin: 'left center',
            display: 'inline-block',
            whiteSpace: 'nowrap',
            transition: 'transform 0.2s ease-in-out'
          }}
        >
          {value}
        </span>
        {measurement && (
          <span className="metric-unit" style={{
            color,
            transform: `scale(${scale})`,
            transformOrigin: 'left center',
            display: 'inline-block',
            marginLeft: scale < 1 ? `${2 * scale}px` : '2px',
            transition: 'transform 0.2s ease-in-out, margin-left 0.2s ease-in-out'
          }}>
            {measurement}
          </span>
        )}
      </div>
      {subtitle && (
        <div className="metric-subtitle">{subtitle}</div>
      )}
    </div>
  );
};

export default KeyMetricCard;

// Export backward compatibility alias
export { KeyMetricCard as KPICard };